import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Mail, Phone, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function Team() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const emptyForm = { name: "", email: "", password: "", role: "technician", phone: "" };
  const [form, setForm] = useState(emptyForm);

  async function load() { const { data } = await api.get("/users"); setUsers(data); }
  useEffect(() => { load(); }, []);

  async function save() {
    try {
      await api.post("/users", form);
      toast.success("Mitarbeiter erstellt");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  }
  async function updateStatus(id, status) {
    await api.patch(`/users/${id}`, { status }); load();
  }
  async function remove(id) {
    if (!window.confirm("Wirklich löschen?")) return;
    await api.delete(`/users/${id}`); load();
  }

  async function resetPassword(u) {
    if (!window.confirm(`Passwort für ${u.name} wirklich zurücksetzen? Es wird ein neues Passwort generiert.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`, {});
      window.prompt(`Neues Passwort für ${u.email}:\n(Bitte jetzt kopieren — wird nicht erneut angezeigt)`, data.new_password);
      toast.success("Passwort zurückgesetzt");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="team-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">Team</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Mitarbeiter</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full h-11 px-6 font-semibold" data-testid="btn-add-user"><Plus className="w-4 h-4 mr-1" /> Mitarbeiter hinzufügen</Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl max-w-md">
            <DialogHeader><DialogTitle>Neuer Mitarbeiter</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="user-name" className="rounded-full h-10 px-4" /></div>
              <div><Label>E-Mail</Label><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="user-email" className="rounded-full h-10 px-4" /></div>
              <div><Label>Passwort</Label><Input type="password" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} data-testid="user-pw" className="rounded-full h-10 px-4" /></div>
              <div><Label>Telefon</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} data-testid="user-phone" className="rounded-full h-10 px-4" /></div>
              <div><Label>Rolle</Label>
                <Select value={form.role} onValueChange={v=>setForm({...form, role:v})}>
                  <SelectTrigger className="rounded-full h-10 px-4" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technician">Techniker</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="customer">Kunde</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="rounded-full" data-testid="user-save">Erstellen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {users.map(u => (
          <Card key={u.id} className="rounded-3xl p-6 border-border/60 glass hover:-translate-y-1 transition" data-testid={`user-${u.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center font-heading font-bold text-xl">
                {u.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-heading font-semibold text-lg">{u.name}</div>
                  {u.is_owner && <Badge className="rounded-full text-[10px] bg-amber-500/20 text-amber-500 border-amber-500/30">AGENTUR-INHABER</Badge>}
                </div>
                <Badge className="rounded-full mt-1 capitalize" variant="secondary">{u.role}</Badge>
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-4 h-4" /><span className="truncate">{u.email}</span></div>
              {u.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-4 h-4" />{u.phone}</div>}
              {u.role === "technician" && (
                <div className="flex items-center justify-between text-xs pt-1">
                  <span className="text-muted-foreground">Agentur-Provision</span>
                  <input type="number" min="0" max="100" step="1" defaultValue={u.commission_rate ?? ""} placeholder="Standard"
                         onBlur={async (e)=>{
                           const v = e.target.value;
                           const val = v === "" ? null : parseFloat(v);
                           await api.patch(`/users/${u.id}`, { commission_rate: val });
                           toast.success(`Provision gesetzt${val != null ? `: ${val}%` : " (Standard)"}`);
                         }}
                         className="w-16 rounded-full h-7 bg-secondary border border-border/60 px-3 text-xs text-right"
                         data-testid={`commission-${u.id}`} />
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <Select value={u.status || "active"} onValueChange={(v)=>updateStatus(u.id, v)} disabled={u.is_owner}>
                <SelectTrigger className="rounded-full h-8 text-xs flex-1" data-testid={`user-status-${u.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="inactive">Inaktiv</SelectItem>
                </SelectContent>
              </Select>
              <Button size="icon" variant="ghost" className="rounded-full" onClick={()=>resetPassword(u)}
                      data-testid={`reset-pw-${u.id}`} title="Passwort zurücksetzen">
                <KeyRound className="w-4 h-4" />
              </Button>
              {!u.is_owner && (
                <Button size="icon" variant="ghost" className="rounded-full" onClick={()=>remove(u.id)} data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
